import { useCallback, useMemo, useState } from 'react';
import BordFlag from 'components/NudosComponents/BordFlag/BordFlag';
import { TcountryCode } from 'components/NudosComponents/BordFlag/BordFlag.types';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import BordStatus from 'components/NudosComponents/BordStatus/BordStatus';
import { STATUS_VARIANTS, statusVariants } from 'components/NudosComponents/BordStatus/BordStatus.type';
import NudosButton from 'components/NudosComponents/NudosButton/NudosButton';
import { generateExportDetailUrl } from 'services/billing/storageBillingInvoices.service';
import { bordColorTheme } from 'styles/styles.constants';
import { INVOICE_STATUS, IStorageBillingInvoices } from 'types/billing.types';
import { ICountryDetail } from 'types/bussines';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { formatCost } from 'utils/formatNumbers';
import { DateTime } from 'luxon';
import BilllingListCardSkeletonLoader from '../BilllingListCardSkeletonLoader/BilllingListCardSkeletonLoader';
import { noDataCopy } from 'constants/constants';
import NudosHoverText from 'components/NudosComponents/NudosHoverText/NudosHoverText';
import './BillingModuleListingCard.scss';

interface ICurrentBillingStatusDisplay {
  label: string;
  variant: statusVariants;
}

interface IBillingModuleListingCardProps {
  billingInvoice?: IStorageBillingInvoices;
  countryList: ICountryDetail[];
  handleModalSide: (billingInvoiceId: number) => void;
  isLoading?: boolean;
}

const BILLING_STATUS_DISPLAY_MAP: Record<INVOICE_STATUS, ICurrentBillingStatusDisplay> = {
  [INVOICE_STATUS.PENDING]: { label: 'Por cargar factura', variant: STATUS_VARIANTS.danger },
  [INVOICE_STATUS.LOADED]: { label: 'Factura cargada', variant: STATUS_VARIANTS.success }
};

const BillingModuleListingCard = ({
  billingInvoice,
  countryList,
  handleModalSide,
  isLoading = false
}: IBillingModuleListingCardProps) => {
  const [isExportDetailLoading, setIsExportDetailLoading] = useState<boolean>(false);

  const {
    storageInvoiceId,
    organizationInfo,
    billingMethod,
    warehouseLocation,
    invoiceNumber,
    amount,
    invoiceUrl,
    proofOfPaymentUrl,
    month,
    currency
  } = billingInvoice || {};

  const billingCountryName = useMemo(() => {
    if (!billingMethod?.billingCountry) return undefined;
    return countryList.find(country => country?.code === billingMethod?.billingCountry)?.name;
  }, [countryList, billingMethod?.billingCountry]);

  const billingStatus = useMemo(() => {
    const status = invoiceUrl ? INVOICE_STATUS.LOADED : INVOICE_STATUS.PENDING;
    return BILLING_STATUS_DISPLAY_MAP[status];
  }, [invoiceUrl]);

  const formatMonthKey = (monthKey: string): string => {
    const [year, month] = monthKey.split('-');
    const date = DateTime.fromObject({ year: Number(year), month: Number(month) });
    const formatted = date.setLocale('es').toFormat('LLLL yyyy');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const handleOpenDocument = useCallback((url: string) => {
    if (!url) return;
    window.open(url, '_blank');
  }, []);

  const handleConsultButtonClick = useCallback(() => {
    if (!storageInvoiceId) return;
    handleModalSide(storageInvoiceId);
  }, [storageInvoiceId, handleModalSide]);

  const handleDownloadReceiptButtonClick = useCallback(async () => {
    if (!proofOfPaymentUrl) return;
    handleOpenDocument(proofOfPaymentUrl);
  }, [proofOfPaymentUrl, handleOpenDocument]);

  const handleOpenExportDetail = useCallback(async () => {
    if (!storageInvoiceId) return;
    try {
      setIsExportDetailLoading(true);
      const { templateUrl } = await generateExportDetailUrl(storageInvoiceId);
      if (templateUrl) {
        handleOpenDocument(templateUrl);
        displaySuccessNotification({
          customJSXMessage: <>Detalle de exportación abierto con éxito</>
        });
      }
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al abrir el detalle de exportación</>
      });
    } finally {
      setIsExportDetailLoading(false);
    }
  }, [storageInvoiceId, handleOpenDocument]);

  if (isLoading) {
    return <BilllingListCardSkeletonLoader />;
  }

  if (!billingInvoice) {
    return null;
  }

  return (
    <div className="billingModuleListingCard withHover" onClick={handleConsultButtonClick}>
      <div className="tableField id">{storageInvoiceId}</div>
      <div className="tableField client">
        <NudosHoverText
          customClassName="title"
          text={organizationInfo?.organizationName || ''}
          charactersLimit={13}
          onlyIfTruncated
        />
        <span className="clientId">ID: {organizationInfo?.organizationId}</span>
      </div>
      <div className="tableField billingEntityBord">
        <NudosHoverText
          customClassName="title"
          text={billingMethod?.beneficiary || ''}
          charactersLimit={20}
          onlyIfTruncated
        />
        <div className="billingCountry">
          <div className="bordFlagContainer">
            <BordFlag country={billingMethod?.billingCountry as TcountryCode} variant="circle" customWidth="14px" />
          </div>
          <NudosHoverText
            customClassName="subTitle"
            text={billingCountryName || ''}
            charactersLimit={16}
            onlyIfTruncated
          />
        </div>
      </div>
      <div className="tableField warehouse">
        <div className="bordFlagContainer">
          <BordFlag country={warehouseLocation?.countryCode as TcountryCode} variant="circle" customWidth="14px" />
        </div>
        <NudosHoverText
          customClassName="subTitle"
          text={warehouseLocation?.country || ''}
          charactersLimit={12}
          onlyIfTruncated
        />
      </div>
      <div className="tableField invoiceNumber">
        <NudosHoverText text={invoiceUrl ? invoiceNumber || '' : ''} charactersLimit={13} onlyIfTruncated />
      </div>
      <div className="tableField month">{month ? formatMonthKey(month) : ''}</div>
      <div className="tableField amount">
        {formatCost(amount, 2)} {currency}
      </div>
      <div className="tableField billlingStatus">
        <BordStatus
          customClassName="customBillingStatus"
          variant={billingStatus?.variant}
          label={billingStatus?.label}
        />
      </div>
      <div className="actions" onClick={e => e.stopPropagation()}>
        <NudosButton
          buttonText="Consultar"
          colorPalette="primary"
          buttonIcon={<BordOneToneIcon variant="plus" customWidth="12px" stroke={bordColorTheme.white[50]} />}
          customClassName="customButton consultButton"
          handleClick={handleConsultButtonClick}
        />
        <NudosButton
          buttonText="Comprobante"
          colorPalette="secondary"
          buttonIcon={
            <BordOneToneIcon variant="download" customWidth="12px" stroke={bordColorTheme.primary.fgDefault} />
          }
          isButtonDisabled={!proofOfPaymentUrl}
          customClassName="customButton receiptButton"
          handleClick={handleDownloadReceiptButtonClick}
        />
        <NudosButton
          buttonText="Exportar detalle"
          colorPalette="secondary"
          buttonIcon={<BordOneToneIcon variant="file" customWidth="12px" stroke={bordColorTheme.primary.fgDefault} />}
          customClassName="customButton"
          handleClick={handleOpenExportDetail}
          isButtonLoading={isExportDetailLoading}
        />
      </div>
    </div>
  );
};

export default BillingModuleListingCard;
